/**
 * 2026-11-01 (owner) · Completed → View Report: the batch's run, step by step, in one modal instead of the old right-hand
 * drawer — what is complete and what is pending, process → operation → step, with who did it, when, what was recorded
 * and who signed. A read of GET /bp/run/batches/<batch>/report/ (services/run/batch_report.py); the client derives nothing.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Collapse, Empty, Progress, Segmented, Skeleton, Tag } from 'antd';
import { ClipboardList } from 'lucide-react';
import DataTable from '@/components/DataTable';
import PageModal from '@/components/PageModal';
import StatCard from '@/components/StatCard';
import { get_api_function } from '@/utils/api';
import { formatDateTime } from '@/utils/datetime';

const STATUS = {
  complete: { label: 'Complete', color: 'success' },
  skipped: { label: 'Skipped', color: 'default' },
  in_progress: { label: 'In progress', color: 'processing' },
  pending: { label: 'Pending', color: 'warning' },
  blocked: { label: 'Blocked', color: 'error' },
};
const FILTERS = { all: () => true, complete: (s) => s.group === 'complete', pending: (s) => !['complete', 'skipped'].includes(s.group) };

const typeWord = (t) => (t || '').toLowerCase().replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
const when = (v) => (v ? formatDateTime(v) : '—');

const columns = [
  { title: '#', key: 'n', width: 48, render: (_, __, i) => i + 1 },
  {
    title: 'Step',
    key: 'step',
    render: (_, s) => (
      <div>
        <div style={{ fontWeight: 600 }}>{s.title}{s.pass ? ` · pass ${s.pass}` : ''}</div>
        <div style={{ fontSize: 12.5, opacity: 0.7 }}>{s.code} · {typeWord(s.step_type)}</div>
      </div>
    ),
  },
  { title: 'Status', key: 'status', width: 120, render: (_, s) => <Tag color={(STATUS[s.group] || STATUS.pending).color}>{(STATUS[s.group] || STATUS.pending).label}</Tag> },
  { title: 'Role', dataIndex: 'role', key: 'role', width: 170, render: (v) => v || '—' },
  { title: 'Done by', dataIndex: 'by', key: 'by', width: 150, render: (v) => v || '—' },
  { title: 'Started', dataIndex: 'started_at', key: 'started_at', width: 150, render: when },
  { title: 'Completed', dataIndex: 'done_at', key: 'done_at', width: 150, render: when },
];

/** What the step recorded and who signed — opened from its row. */
function StepDetail({ step }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 16, fontSize: 14 }}>
      <div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Recorded</div>
        {step.values.length ? step.values.map((v, i) => (
          <div key={`${v.label}-${i}`} style={{ display: 'flex', gap: 8, padding: '2px 0' }}>
            <span style={{ opacity: 0.7, flex: '0 0 40%' }}>{v.label}</span>
            <span style={{ overflowWrap: 'anywhere' }}>{v.value || '—'}{v.verdict ? ` · ${v.verdict}` : ''}</span>
          </div>
        )) : <span style={{ opacity: 0.7 }}>Nothing recorded</span>}
      </div>
      <div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Signatures</div>
        {step.signatures.length ? step.signatures.map((s, i) => (
          <div key={`${s.meaning}-${i}`} style={{ padding: '2px 0' }}>{s.meaning}: {s.by} · {when(s.at)}</div>
        )) : <span style={{ opacity: 0.7 }}>None</span>}
      </div>
    </div>
  );
}

const hasDetail = (s) => s.values.length > 0 || s.signatures.length > 0;

function OperationSteps({ steps }) {
  return (
    <DataTable
      bordered
      pagination={false}
      height={null}
      showCount={false}
      rowKey="id"
      columns={columns}
      data={steps}
      expandable={{ expandedRowRender: (s) => <StepDetail step={s} />, rowExpandable: hasDetail }}
    />
  );
}

export default function BatchReportModal({ batchNumber, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!batchNumber) return undefined;
    const ctrl = new AbortController();
    setLoading(true); setData(null); setError(''); setFilter('all');
    get_api_function(
      `/run/batches/${encodeURIComponent(batchNumber)}/report/`,
      (res) => {
        if (res?.data?.status === 'success') setData(res.data.data);
        else setError(res?.data?.message || 'The batch report could not be read.');
        setLoading(false);
      },
      {},
      ctrl.signal,
      (err) => {
        if (ctrl.signal.aborted) return;
        setError(err?.response?.data?.message || 'The batch report could not be read.');
        setLoading(false);
      },
    );
    return () => ctrl.abort();
  }, [batchNumber, attempt]);

  // Operations with steps left to do start open; a finished batch opens on its first operation.
  const shown = useMemo(() => (data?.processes || []).map((p) => ({
    ...p,
    operations: p.operations
      .map((o) => ({ ...o, shown: o.steps_list.filter(FILTERS[filter]) }))
      .filter((o) => filter === 'all' || o.shown.length),
  })).filter((p) => p.operations.length), [data, filter]);
  const openKeys = useMemo(() => {
    const ops = shown.flatMap((p) => p.operations);
    const unfinished = ops.filter((o) => o.pending > 0).map((o) => String(o.id));
    return unfinished.length ? unfinished : ops.slice(0, 1).map((o) => String(o.id));
  }, [shown]);

  const s = data?.summary;
  const h = data?.header;
  const percent = s?.steps ? Math.round((s.done / s.steps) * 100) : 0;
  const fact = (label, value) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12.5, opacity: 0.7 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, overflowWrap: 'anywhere' }}>{value || '—'}</div>
    </div>
  );

  return (
    <PageModal
      open={!!batchNumber}
      onCancel={onClose}
      icon={<ClipboardList size={22} />}
      title={`Batch report · ${batchNumber || ''}`}
      description="What is complete and what is pending, step by step."
      width={1180}
      destroyOnHidden
    >
      {loading ? <Skeleton active paragraph={{ rows: 8 }} /> : null}
      {error ? (
        <Alert type="error" showIcon message={error}
               action={<Button size="small" onClick={() => setAttempt((n) => n + 1)}>Retry</Button>} />
      ) : null}
      {data ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontSize: 14 }} data-testid="batch-report">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
            {fact('Master record', h.record?.name)}
            {fact('Product', h.product)}
            {fact('Batch size', h.batch_size)}
            {fact('Status', h.status)}
            {fact('Started', when(h.started_at))}
            {fact('Finished', when(h.finished_at))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
            <StatCard label="Total steps" value={s.steps} />
            <StatCard label="Complete" value={s.complete} />
            <StatCard label="Pending" value={s.pending} />
            <StatCard label="Operations done" value={`${s.operations_done} / ${s.operations}`} />
          </div>
          <Progress percent={percent} aria-label="Steps complete" />
          <Segmented
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: `All (${s.steps})` },
              { value: 'complete', label: `Complete (${s.complete})` },
              { value: 'pending', label: `Pending (${s.pending})` },
            ]}
            style={{ alignSelf: 'flex-start' }}
          />
          {!shown.length ? <Empty description={filter === 'pending' ? 'Nothing is pending on this batch' : 'No steps to show'} /> : null}
          {shown.map((p) => (
            <section key={p.id ?? p.name}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{p.name || 'Process'}</span>
                <span style={{ opacity: 0.7 }}>{p.done} of {p.steps} steps done</span>
              </div>
              <Collapse
                key={`${filter}-${p.id}`}
                defaultActiveKey={openKeys}
                items={p.operations.map((o) => ({
                  key: String(o.id),
                  label: <span style={{ fontWeight: 600 }}>{o.number}. {o.name}</span>,
                  extra: o.steps
                    ? <Tag color={o.pending ? 'warning' : 'success'}>{o.pending ? `${o.pending} pending` : 'Complete'}</Tag>
                    : <Tag>No step records</Tag>,
                  children: o.steps_list.length
                    ? <OperationSteps steps={o.shown} />
                    : <Empty description="This operation has no step records." />,
                }))}
              />
            </section>
          ))}
        </div>
      ) : null}
    </PageModal>
  );
}
